import { Core, Decorative } from "@faire/design-tokens";
import { trackRetailerNotificationsUserAccountClick } from "@faire/web-api/events/retailerNotifications/click/userAccount";
import profileSettings from "@faire/web-api/routes/retailer/settings/profile";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Account } from "@faire/web/slate/icons/foundation/Account";
import { Spacer } from "@faire/web/slate/spacing";
import { useViewportWidth } from "@faire/web/ui/hooks/useViewportWidth";
import { Link } from "@faire/web/ui/LinkAnchor";
import React, { Suspense, useState } from "react";

import { topNavEvent } from "@faire/retailer/components/Header/Events";
import { LazyHeaderAccount } from "@faire/retailer/components/Header/HeaderAccount/index";
import { HeaderClickItem } from "@faire/retailer/components/Header/HeaderClickItem";
import { HeaderHoverItem } from "@faire/retailer/components/Header/HeaderHoverItem";
import { IconWithMaybeDot } from "@faire/retailer/components/Header/icons/IconWithMaybeDot";
import {
  HeaderDropdown,
  HeaderIconContainer,
  NAVIGATION_REDESIGN_MAX_HEADER_WIDTH,
} from "@faire/retailer/components/Header/styled";
import { useIsHeaderDropdownVisible } from "@faire/retailer/components/HeaderPriorityRenderer/useIsHeaderDropdownVisible";
import { useHasAccountNotifications } from "@faire/retailer/hooks/accountMenu/useHasAccountNotifications";
import { useInsiderNotificationType } from "@faire/retailer/hooks/accountMenu/useInsiderNotificationType";
import { useRevampedDropdownData } from "@faire/retailer/hooks/accountMenu/useRevampedDropdownData";
import { NotificationDot } from "@faire/retailer/packages/messenger/views/NotificationDot";
import { getSettingEnableRetailerNotification } from "@faire/retailer/settings/getSettingEnableRetailerNotification";
import { useSettingChangeBagToCartCopyAndIcon } from "@faire/retailer/settings/useSettingChangeBagToCartCopyAndIcon";
import { useSettingGlobalNavigationRedesign } from "@faire/retailer/settings/useSettingGlobalNavigationRedesign";
import { useRetailerStore } from "@faire/retailer/stores/domain/RetailerStore";
import { useMessengerFactory } from "@faire/retailer/stores/MessengerFactory";

export const HeaderAccountDropdown = () => {
  const [forceHideDropdown, setForceHideDropdown] = useState(false);
  const { strictLocalize } = useStrictLocalization();
  const { retailer } = useRetailerStore(["retailer"]);
  const [shouldShowDropdown, setShouldShowDropdown] = useState(false);
  const {
    messengerStore: { unreadConversationCount },
  } = useMessengerFactory(["messengerStore"]);
  const isSSR = useIsSSR();
  const showNotification = useHasAccountNotifications();
  const { setHeaderDropdownVisibility } = useIsHeaderDropdownVisible();
  const viewportWidth = useViewportWidth((width) => width);
  const changeBagToCart = useSettingChangeBagToCartCopyAndIcon();

  const hasInsiderTermsAndConditionsNotification =
    !!useInsiderNotificationType();
  // prefetch the data for the dropdown that is used in the child component
  useRevampedDropdownData();

  const shouldUseRedesignExperimentSpacing =
    useSettingGlobalNavigationRedesign();

  // place the dropdown at the right edge of the header
  const rightPosition =
    !shouldUseRedesignExperimentSpacing ||
    viewportWidth <= NAVIGATION_REDESIGN_MAX_HEADER_WIDTH
      ? 0
      : (viewportWidth - NAVIGATION_REDESIGN_MAX_HEADER_WIDTH) / 2;

  if (retailer === undefined) {
    return null;
  }

  const toggleHandler = (shouldShowDropdown: boolean) => {
    setShouldShowDropdown(shouldShowDropdown);
    setHeaderDropdownVisibility("ACCOUNT", shouldShowDropdown);
  };

  const accountDropdown = (
    <HeaderDropdown
      $right={`${rightPosition}px`}
      $width={getSettingEnableRetailerNotification() ? "288px" : "270px"}
      $maxHeight="calc(100vh - 110px)"
    >
      <Suspense fallback={null}>
        <LazyHeaderAccount
          onItemClick={() =>
            getSettingEnableRetailerNotification()
              ? setShouldShowDropdown(false)
              : setForceHideDropdown(true)
          }
        />
      </Suspense>
    </HeaderDropdown>
  );

  const getSpacer = () => {
    if (shouldUseRedesignExperimentSpacing) {
      if (changeBagToCart) {
        return <Spacer size={12} />;
      }
      return <Spacer size="2x" />;
    }
    return null;
  };

  if (getSettingEnableRetailerNotification()) {
    return (
      <>
        <HeaderClickItem
          clickChild={accountDropdown}
          dataTestId="accountDropdown"
          ariaLabel={strictLocalize({
            defaultMessage: "My Profile",
            description: { text: "Link to my profile page" },
          })}
          onClick={trackRetailerNotificationsUserAccountClick}
          toggleDropdown={toggleHandler}
          shouldShowDropdown={shouldShowDropdown}
        >
          <HeaderIconContainer>
            <IconWithMaybeDot
              icon={<Account strokeWidth={0.8} fontSize="24px" />}
              shouldAnimateDot
              shouldShowDot={unreadConversationCount > 0}
              isActive={shouldShowDropdown}
              delayAnimationTime={750}
            />
          </HeaderIconContainer>
        </HeaderClickItem>
        {getSpacer()}
      </>
    );
  }

  return (
    <HeaderHoverItem
      keepHiddenAfterClick
      onHover={topNavEvent("account")}
      onBlur={() => setForceHideDropdown(false)}
      hoverChild={forceHideDropdown ? null : accountDropdown}
      dataTestId="accountDropdown"
      ariaLabel={strictLocalize({
        defaultMessage: "My Profile",
        description: { text: "Link to my profile page" },
      })}
    >
      <Link to={profileSettings()}>
        <HeaderIconContainer>
          {!isSSR && showNotification ? (
            <NotificationDot
              $right={5}
              $color={
                hasInsiderTermsAndConditionsNotification
                  ? Decorative.shade.orange
                  : Core.icon.default
              }
              data-test-id="notification-dot"
            />
          ) : null}
          <Account strokeWidth={2} fontSize="16px" />
        </HeaderIconContainer>
      </Link>
    </HeaderHoverItem>
  );
};
